<template>
  <PageHeader>
    <template #header-title>{{ $t('Candidates') }}</template>
    <template v-if="!candidatesLoadingError && !loading" #header-actions>
      <WdsButton
        v-if="user.hasPermission('manage-candidates')"
        primary
        data-test="add-supplier-candidate-button"
        @click="showModal = true"
      >
        {{ $t('Add Candidate') }}
      </WdsButton>

      <Teleport to="#modals">
        <RecruiterFreelancerModal
          :show="showModal"
          :on-cancel="() => (showModal = false)"
          :on-confirm="onConfirm"
          :currency="props.supplierCurrency"
          :title="$t('Add Candidate')"
          :save-button-text="$t('Add')"
          :save-button-text-while-working="$t('Adding')"
          :help-text="$t('The candidate will not be contacted before they have been selected for a job.')"
          :manages-workers="false"
        />
      </Teleport>
    </template>
  </PageHeader>
  <div>
    <div v-if="candidatesLoadingError" class="tw-mt-9">
      <ContentLoadingErrorNotice />
    </div>
    <template v-else>
      <div>
        <RecruiterFreelancerContacts
          :candidates="result?.supplierCandidates?.data ?? []"
          :apollo-loading="loading"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { WdsButton } from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { ref } from 'vue'
import gql from 'graphql-tag'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import RecruiterFreelancerContacts from '@/components/Contacts/RecruiterFreelancer/RecruiterFreelancerContacts.vue'
import RecruiterFreelancerModal from '@/components/Contacts/RecruiterFreelancer/RecruiterFreelancerModal.vue'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { user } from '@/composables/useUser'
import PageHeader from '@/components/PageHeader.vue'

const props = defineProps({
  supplierAccountId: {
    type: String,
    required: true,
  },
  supplierCurrency: {
    type: String,
    default: 'DKK',
  },
})

const candidatesLoadingError = ref(false)
const showModal = ref(false)

const GET_SUPPLIER_CANDIDATES = gql`
  query GetSupplierCandidates($first: Int!, $accounts: [ID!]) {
    supplierCandidates(first: $first, accounts: $accounts) {
      data {
        id
        email
        status
        hourRate: rate(rateType: HOURLY)
        dailyRate: rate(rateType: DAILY)
        monthlyRate: rate(rateType: MONTHLY)
        jobTitle
        links
        tags {
          id
          name
        }
        files {
          data {
            id
            name
          }
        }
        worker {
          id
          name
          firstName
          middleName
          lastName
          avatar
          currency
          profile {
            url
          }
        }
      }
    }
  }
`

const CREATE_SUPPLIER_CANDIDATE = gql`
  mutation CreateSupplierCandidate($input: CreateSupplierCandidateInput!) {
    createSupplierCandidate(input: $input) {
      id
    }
  }
`

const {
  result,
  loading,
  onError: onQueryError,
} = useQuery(
  GET_SUPPLIER_CANDIDATES,
  { first: PER_PAGE_MAXIMUM, accounts: [props.supplierAccountId] },
  {
    clientId: 'publicApi',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  },
)

onQueryError((error: Error) => {
  if (error instanceof SilentNetworkError) return
  candidatesLoadingError.value = true
})

const { mutate: mutateAddCandidate } = useMutation(CREATE_SUPPLIER_CANDIDATE, {
  clientId: 'publicApi',
  refetchQueries: ['GetSupplierCandidates'],
})

async function onConfirm(data) {
  await mutateAddCandidate({
    input: {
      supplier: props.supplierAccountId,
      currency: props.supplierCurrency,
      ...data,
    },
  })
  showModal.value = false
}
</script>
